import { useState } from 'react';
import { Menu, X, BarChart3, Settings, FileText, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DevelopmentDashboard } from '@/components/DevelopmentDashboard';

type AdminTab = 'progress' | 'contacts' | 'templates' | 'campaigns' | 'automations' | 'settings';

export default function Admin() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<AdminTab>('progress');

  const navItems = [
    {
      id: 'progress',
      label: 'Progresso',
      icon: <BarChart3 className="w-5 h-5" />,
      description: 'Acompanhe o desenvolvimento',
    },
    {
      id: 'contacts',
      label: 'Contatos',
      icon: <FileText className="w-5 h-5" />,
      description: 'Gerenciar contatos',
    },
    {
      id: 'templates',
      label: 'Templates',
      icon: <FileText className="w-5 h-5" />,
      description: 'Criar templates',
    },
    {
      id: 'campaigns',
      label: 'Campanhas',
      icon: <Zap className="w-5 h-5" />,
      description: 'Disparar campanhas',
    },
    {
      id: 'automations',
      label: 'Automações',
      icon: <Zap className="w-5 h-5" />,
      description: 'Criar automações',
    },
    {
      id: 'settings',
      label: 'Configurações',
      icon: <Settings className="w-5 h-5" />,
      description: 'Configurar sistema',
    },
  ];

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-20'
        } bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-gray-900">WhatsApp</h1>
                <p className="text-xs text-gray-500">Automation</p>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id as AdminTab)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                activeTab === item.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              title={item.label}
            >
              {item.icon}
              {sidebarOpen && (
                <div className="text-left">
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
              )}
            </button>
          ))}
        </nav>

        {/* Toggle Button */}
        <div className="p-4 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="w-full"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {navItems.find((item) => item.id === activeTab)?.label}
              </h2>
              <p className="text-sm text-gray-600">
                {navItems.find((item) => item.id === activeTab)?.description}
              </p>
            </div>
            <Button variant="outline" size="sm">
              Ajuda
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {activeTab === 'progress' && <DevelopmentDashboard />}
          {activeTab === 'contacts' && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Gerenciador de Contatos - Em desenvolvimento</p>
              <Button disabled>Voltar em breve</Button>
            </div>
          )}
          {activeTab === 'templates' && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Editor de Templates - Em desenvolvimento</p>
              <Button disabled>Voltar em breve</Button>
            </div>
          )}
          {activeTab === 'campaigns' && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Gerenciador de Campanhas - Em desenvolvimento</p>
              <Button disabled>Voltar em breve</Button>
            </div>
          )}
          {activeTab === 'automations' && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Editor de Automações - Em desenvolvimento</p>
              <Button disabled>Voltar em breve</Button>
            </div>
          )}
          {activeTab === 'settings' && (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Configurações - Em desenvolvimento</p>
              <Button disabled>Voltar em breve</Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
